import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FormInstance, FormRule } from 'antd';
import { Checkbox, Form, Input } from 'antd';
import cn from 'classnames';
import type { AddressRes } from 'api/swagger';
import { formatCoordinates } from 'shared/utils/coordinates';
import { CargoAddressFinder } from 'components/cargos/cargo-form/components/cargo-address-finder';
import { CountryFormSelect } from 'components/common/country-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import { optionalCoordinatesRule } from '../../utils';
import styles from './cargo-address-form-block.module.scss';

interface ICargoAddressFormBlockProps {
  form: FormInstance;
  prefix:
    | 'unloadingAddress'
    | 'loadingAddress'
    | 'assetReturnAddress'
    | 'unloadingCustomAddress'
    | 'loadingCustomAddress';
  withAdditionalInfo?: boolean;
  dateInputName?: string;
}

const addressFieldsRule: Record<string, FormRule[]> = {
  name: [{ required: true, type: 'string' }],
  countryId: [{ required: true, type: 'number' }],
  postCode: [{ required: true, type: 'string' }],
  city: [{ required: true, type: 'string' }],
  address: [{ required: true, type: 'string' }],
  description: [{ required: false, type: 'string' }],
  referenceNumber: [{ required: false, type: 'string' }],
};

export const CargoAddressFormBlock: React.FC<ICargoAddressFormBlockProps> = (props) => {
  const { form, prefix, withAdditionalInfo = false, dateInputName } = props;
  const { t } = useTranslation();

  const handleSelectAddress = (address: AddressRes) => {
    form.setFieldValue([prefix, 'name'], address.name);
    form.setFieldValue([prefix, 'countryId'], address.countryId);
    form.setFieldValue([prefix, 'postCode'], address.postCode);
    form.setFieldValue([prefix, 'city'], address.city);
    form.setFieldValue([prefix, 'address'], address.address);
    form.setFieldValue([prefix, 'coordinates'], formatCoordinates(address.lat, address.lng));
  };

  return (
    <>
      <div className={withAdditionalInfo ? styles.addressWrap : cn(styles.addressWrap, styles.lgMarginBottom)}>
        <div className={styles.mainBox}>
          <div className={styles.mainBoxRow}>
            <CargoAddressFinder handleSelect={handleSelectAddress} className={styles.rightMargin} />
            <Form.Item
              name={[prefix, 'name']}
              rules={addressFieldsRule.name}
              className={styles.nameInput}
              rootClassName={styles.marginNone}>
              <Input placeholder={t('cargo:address_name')} />
            </Form.Item>
            <CountryFormSelect
              name={[prefix, 'countryId']}
              rules={addressFieldsRule.countryId}
              className={styles.countryInput}
              rootClassName={styles.marginNone}
              placeholder={t('common:country')}
            />
            <Form.Item
              name={[prefix, 'postCode']}
              rules={addressFieldsRule.postCode}
              className={styles.postcodeInput}
              rootClassName={styles.marginNone}>
              <Input placeholder={t('common:post_code')} />
            </Form.Item>
          </div>
          <div className={styles.mainBoxRow}>
            <Form.Item
              name={[prefix, 'city']}
              rules={addressFieldsRule.city}
              className={styles.cityInput}
              rootClassName={styles.marginNone}>
              <Input placeholder={t('common:city')} />
            </Form.Item>
            <Form.Item
              name={[prefix, 'address']}
              rules={addressFieldsRule.address}
              className={styles.addressInput}
              rootClassName={styles.marginNone}>
              <Input placeholder={t('common:address')} />
            </Form.Item>
            <Form.Item
              name={[prefix, 'coordinates']}
              rules={[optionalCoordinatesRule(t('trip:coordinates_format_error'))]}
              className={styles.coordinatesInput}
              rootClassName={styles.marginNone}>
              <Input placeholder={t('trip:coordinates_placeholder')} />
            </Form.Item>
          </div>
        </div>
        <div className={styles.descBox}>
          <Form.Item
            name={[prefix, 'description']}
            rules={addressFieldsRule.description}
            rootClassName={styles.marginNone}>
            <Input.TextArea rows={3} placeholder={t('common:description')} />
          </Form.Item>
        </div>
      </div>
      {withAdditionalInfo ? (
        <div className={cn(styles.addressInfoWrap, styles.lgMarginBottom)}>
          {dateInputName ? <DataRangeFormPicker name={dateInputName} className={styles.infoInput} isRequired /> : null}
          <Form.Item
            name={[prefix, 'referenceNumber']}
            rules={addressFieldsRule.referenceNumber}
            rootClassName={styles.infoInput}>
            <Input placeholder={t('cargo:reference_number')} />
          </Form.Item>
          <Form.Item name={[prefix, 'extraData']} rootClassName={styles.marginNone}>
            <Checkbox.Group>
              <Checkbox value='truck_with_lift' style={{ lineHeight: '32px' }}>
                {t('cargo:with_lift_checkbox')}
              </Checkbox>
              <Checkbox value='booking' style={{ lineHeight: '32px' }}>
                {t('cargo:booking_checkbox')}
              </Checkbox>
              <Checkbox value='fix_time' style={{ lineHeight: '32px' }}>
                {t('cargo:fix_time_checkbox')}
              </Checkbox>
              <Checkbox value='attention' style={{ lineHeight: '32px' }}>
                {t('cargo:attention_checkbox')}
              </Checkbox>
            </Checkbox.Group>
          </Form.Item>
        </div>
      ) : null}
    </>
  );
};
